import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchMe, loginEmail, registerEmail, forcePasswordUpdate as forcePasswordUpdateRequest, logoutRequest, tokenStore } from "../lib/api";
import { clearExplicitDismissal } from "../hooks/useInstallPrompt";
import { trySilentSso } from "../lib/silentSso";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const data = await fetchMe();
            setUser(data);
        } catch (e) {
            // A token this app saved earlier is no longer accepted (the Super App session changed, e.g. demo was switched on): drop it and
            // ask the common login page for the current session instead of showing the signed-out screen to someone who is signed in.
            if (e?.response?.status === 401 && tokenStore.get()) {
                tokenStore.clear();
                try { sessionStorage.removeItem("hv_sso_checked"); } catch { /* ignore */ }
                trySilentSso({ hasToken: () => false, setToken: tokenStore.set });
            }
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    // Signing out of the Super App ends every app's session. When this tab comes back into view, ask the gateway once whether the session is
    // still alive: a rejected token (401) means the person signed out elsewhere, so this app shows the signed-out screen too.
    useEffect(() => {
        let last = 0;
        const recheck = () => {
            if (document.visibilityState === "hidden" || !tokenStore.get() || Date.now() - last < 10000) return;
            last = Date.now();
            fetchMe().catch((e) => { if (e?.response?.status === 401) { tokenStore.clear(); setUser(null); } });
        };
        window.addEventListener("focus", recheck);
        document.addEventListener("visibilitychange", recheck);
        return () => { window.removeEventListener("focus", recheck); document.removeEventListener("visibilitychange", recheck); };
    }, []);

    // The gateway only returns { token, user_id }. Persist the token before
    // anything else (or the next request goes out unauthenticated), then
    // hydrate the Poems-shaped user from the proxied /auth/me — its first
    // call JIT-creates the guardian account for a brand-new user.
    const adopt = useCallback(async (data) => {
        tokenStore.set(data.token);
        const me = await fetchMe();
        setUser(me);
        return me;
    }, []);

    const signIn = useCallback(async (email, password) => {
        const data = await loginEmail({ email, password });
        if (data.password_reset_required) return data; // { password_reset_required, reset_token }
        return adopt(data);
    }, [adopt]);

    const forcePasswordUpdate = useCallback(async (resetToken, newPassword) => {
        return adopt(await forcePasswordUpdateRequest(resetToken, newPassword));
    }, [adopt]);

    const signUp = useCallback(async (name, email, password) => {
        return adopt(await registerEmail({ name, email, password }));
    }, [adopt]);

    const logout = async () => {
        // Clear locally first: even if the network call fails, this browser
        // must stop presenting the token.
        tokenStore.clear();
        try { await logoutRequest(); } catch { /* ignore */ }
        setUser(null);
        // A stale active-learner id left in localStorage would otherwise get
        // sent as learner_id on every request after logout — the backend
        // correctly rejects it ("Learner not found") since there's no
        // guardian to own it anymore, breaking anonymous free-preview access.
        localStorage.removeItem("lv.activeLearner");
        // The "Install app" banner/popup remembers being dismissed (see
        // useInstallPrompt.js) — but that dismissal was this guardian's
        // choice, not a permanent one. Clearing it on sign-out means the
        // next guardian who signs into this browser (or this same one
        // signing back in later) gets offered the install prompt again
        // instead of it staying silently gone forever.
        clearExplicitDismissal();
    };

    return (
        <AuthContext.Provider value={{ user, setUser, loading, checkAuth, signIn, forcePasswordUpdate, signUp, logout, adopt }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
